import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from '@/app/store/hooks';
import { selectIsMegaAdmin, selectUser } from '@/features/authentication';
import { useMenu } from '../../hooks/useMenu';
import { useOrganizationBrand } from '../../hooks/useOrganizationBrand';
import { collectExpandableIds } from '../../utils/menuActivity';
import { SidebarEntities } from './SidebarEntities';
import { SidebarFooter } from './SidebarFooter';
import { SidebarMenu } from './SidebarMenu';
import { SidebarOrgSwitcher } from './SidebarOrgSwitcher';
import { SidebarEmpty, SidebarError, SidebarLoading } from './SidebarStatus';

interface SidebarProps {
  isCollapsed: boolean;
}

export function Sidebar({ isCollapsed }: SidebarProps) {
  const { items, loading, error, retry } = useMenu();
  /* Mega Admin's header, and the only role that fetches it -- see useOrganizationBrand. */
  const org = useOrganizationBrand();

  /*
    Every other role's header: the entities that role is assigned to.

    Mega Admin is excluded by name rather than by emptiness. It holds no assignments by design, so the
    list would be empty anyway and nothing would render; saying so here is what keeps that a decision
    rather than a coincidence that a future seed could quietly undo.
  */
  const isMegaAdmin = useAppSelector(selectIsMegaAdmin);
  const user = useAppSelector(selectUser);
  const entities = isMegaAdmin ? [] : (user?.entityDetail ?? []);
  const { pathname } = useLocation();
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set());

  // Union rather than replace, so a branch the user opened by hand stays open
  // after navigating and multiple parents expand independently.
  useEffect(() => {
    const auto = collectExpandableIds(items, pathname);
    if (auto.length === 0) return;
    setExpandedIds((previous) => new Set([...previous, ...auto]));
  }, [items, pathname]);

  const onToggle = useCallback((id: string) => {
    setExpandedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  return (
    <aside
      id="app-sidebar"
      className={`sidebar${isCollapsed ? ' sidebar--collapsed' : ''}`}
    >
      {/*
        One header, whichever applies.

        The entity dropdown took the organization row's place: that row showed DEFAULT_ORG -- a bundled
        name and badge -- for every role but Mega Admin, so above a real fetched entity it was both
        redundant and, for a second tenant, wrong.

        Mega Admin still gets the row, because it is the one role with no assignments and therefore
        nothing for the dropdown to show; it is also the only role that fetches its organization, so the
        row is real there. No role sees both, and none sees neither.
      */}
      {isMegaAdmin ? (
        <SidebarOrgSwitcher org={org} isCollapsed={isCollapsed} />
      ) : (
        <SidebarEntities entities={entities} isCollapsed={isCollapsed} />
      )}

      <nav className="sidebar__nav" aria-label="Main navigation">
        {loading ? (
          <SidebarLoading isCollapsed={isCollapsed} />
        ) : error ? (
          <SidebarError message={error} onRetry={retry} isCollapsed={isCollapsed} />
        ) : items.length === 0 ? (
          <SidebarEmpty isCollapsed={isCollapsed} />
        ) : (
          <SidebarMenu
            items={items}
            isCollapsed={isCollapsed}
            expandedIds={expandedIds}
            onToggle={onToggle}
          />
        )}
      </nav>

      <SidebarFooter isCollapsed={isCollapsed} />
    </aside>
  );
}
